import { z } from "zod";
import { buildRecordFromKeyList } from "../build-record-from-key-list";
import { buildEnvelopeRetrofitParametersSchema } from "./build-envelope-retrofit-parameters-schema";
import { computeHeatingSystemExchangeConversionFactor } from "./compute-heating-system-exchange-conversion-factor";
import { FACTOR_MODAL_SPLIT_MODE_LIST } from "./factor-modal-split-mode";
import { HEAT_CARRIER_KEY_LIST } from "./heat-carrier-key";
import { HEATING_SYSTEM_EXCHANGE_SYSTEM_BY_KEY } from "./heating-system-exchange-system-by-key";
import { HEATING_SYSTEM_EXCHANGE_SYSTEM_KEY_LIST } from "./heating-system-exchange-system-key-list";
import { isHeatingSystemExchangeSystemKey } from "./is-heating-system-exchange-system-key";
import { MODAL_SPLIT_MODE_LIST } from "./modal-split-mode";
import { ADMINISTRATIVE_BUILDING_ENVELOPE_RETROFIT_PROFILE } from "./administrative-building-envelope-retrofit-profile";
import { OTHER_MUNICIPAL_BUILDING_ENVELOPE_RETROFIT_PROFILE } from "./other-municipal-building-envelope-retrofit-profile";
import { SCHOOL_DAYCARE_ENVELOPE_RETROFIT_PROFILE } from "./school-daycare-envelope-retrofit-profile";
import { computeStockConsumptionKwhPerHour, NEW_STREET_LIGHT_TYPE_KEY_LIST, OLD_STREET_LIGHT_TYPE_KEY_LIST, STREET_LIGHT_TYPE_KEY_LIST, } from "./street-light-type";
const percentage = z.number().min(0).max(100);
const nonNegativeCount = z.number().int().min(0);
export const HeatBehaviourChangeParametersSchema = z.object({
    reductionPercentage: percentage,
});
const StockCountByTypeSchema = z.object(buildRecordFromKeyList(STREET_LIGHT_TYPE_KEY_LIST, () => nonNegativeCount));
const ReplacementCountByNewTypeSchema = z.object(buildRecordFromKeyList(NEW_STREET_LIGHT_TYPE_KEY_LIST, () => nonNegativeCount));
const ReplacementCountByOldTypeSchema = z.object(buildRecordFromKeyList(OLD_STREET_LIGHT_TYPE_KEY_LIST, () => ReplacementCountByNewTypeSchema));
export const STREET_LIGHTING_ISSUE_KEY = {
    TOTAL_STOCK_WATTAGE_ZERO: "totalStockWattageZero",
    REPLACEMENT_EXCEEDS_STOCK: "replacementExceedsStock",
};
export const StreetLightingConversionParametersSchema = z
    .object({
    stockCountByType: StockCountByTypeSchema,
    replacementCountByOldType: ReplacementCountByOldTypeSchema,
})
    .superRefine((parameters, ctx) => {
    if (computeStockConsumptionKwhPerHour(parameters.stockCountByType) <= 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["stockCountByType"],
            params: { messageKey: STREET_LIGHTING_ISSUE_KEY.TOTAL_STOCK_WATTAGE_ZERO },
        });
    }
    OLD_STREET_LIGHT_TYPE_KEY_LIST.forEach((oldKey) => {
        const replacedCount = NEW_STREET_LIGHT_TYPE_KEY_LIST.reduce((sum, newKey) => sum + parameters.replacementCountByOldType[oldKey][newKey], 0);
        if (replacedCount > parameters.stockCountByType[oldKey]) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["replacementCountByOldType", oldKey],
                params: { messageKey: STREET_LIGHTING_ISSUE_KEY.REPLACEMENT_EXCEEDS_STOCK },
            });
        }
    });
});
export const SchoolDaycareEnvelopeRetrofitParametersSchema = buildEnvelopeRetrofitParametersSchema(SCHOOL_DAYCARE_ENVELOPE_RETROFIT_PROFILE);
export const TransportPowertrainMixParametersSchema = z.object({
    targetElectricPercentage: z.number().gt(0).max(100),
});
export const AdministrativeBuildingEnvelopeRetrofitParametersSchema = buildEnvelopeRetrofitParametersSchema(ADMINISTRATIVE_BUILDING_ENVELOPE_RETROFIT_PROFILE);
export const OtherMunicipalBuildingEnvelopeRetrofitParametersSchema = buildEnvelopeRetrofitParametersSchema(OTHER_MUNICIPAL_BUILDING_ENVELOPE_RETROFIT_PROFILE);
const FULL_PERCENTAGE = 100;
const TARGET_PERCENTAGE_SUM_TOLERANCE = 0.01;
const isHundredPercent = (percentageSum) => Math.abs(percentageSum - FULL_PERCENTAGE) <=
    TARGET_PERCENTAGE_SUM_TOLERANCE + Number.EPSILON * Math.max(Math.abs(percentageSum), FULL_PERCENTAGE);
export const HEAT_MIX_SWITCH_ISSUE_KEY = {
    TARGET_PERCENTAGE_SUM_NOT_HUNDRED: "targetPercentageSumNotHundred",
};
export const HeatMixSwitchParametersSchema = z
    .object({
    targetPercentageByCarrier: z.object(buildRecordFromKeyList(HEAT_CARRIER_KEY_LIST, () => percentage)),
})
    .superRefine((parameters, ctx) => {
    const percentageSum = HEAT_CARRIER_KEY_LIST.reduce((sum, carrierKey) => sum + parameters.targetPercentageByCarrier[carrierKey], 0);
    if (!isHundredPercent(percentageSum)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["targetPercentageByCarrier"],
            params: { messageKey: HEAT_MIX_SWITCH_ISSUE_KEY.TARGET_PERCENTAGE_SUM_NOT_HUNDRED },
        });
    }
});
export const TRANSPORT_MODAL_SPLIT_ISSUE_KEY = {
    MODAL_SPLIT_SUM_NOT_HUNDRED: "modalSplitSumNotHundred",
    BASE_MODAL_SHARE_BELOW_MINIMUM: "baseModalShareBelowMinimum",
};
const MIN_BASE_MODAL_SHARE = 0.1;
const MODAL_SPLIT_FIELD_LIST = ["baseModalSplit", "targetModalSplit"];
const ModalSplitSchema = z.object(buildRecordFromKeyList(MODAL_SPLIT_MODE_LIST, () => percentage));
const sumModalSplit = (modalSplit) => MODAL_SPLIT_MODE_LIST.reduce((sum, mode) => sum + modalSplit[mode], 0);
export const TransportModalSplitParametersSchema = z
    .object({
    baseModalSplit: ModalSplitSchema,
    targetModalSplit: ModalSplitSchema,
    supplyElasticity: z.number().min(0).max(1),
})
    .superRefine((parameters, ctx) => {
    MODAL_SPLIT_FIELD_LIST.forEach((field) => {
        if (isHundredPercent(sumModalSplit(parameters[field])))
            return;
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [field],
            params: { messageKey: TRANSPORT_MODAL_SPLIT_ISSUE_KEY.MODAL_SPLIT_SUM_NOT_HUNDRED },
        });
    });
    FACTOR_MODAL_SPLIT_MODE_LIST.forEach((mode) => {
        if (parameters.baseModalSplit[mode] >= MIN_BASE_MODAL_SHARE)
            return;
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["baseModalSplit", mode],
            params: {
                messageKey: TRANSPORT_MODAL_SPLIT_ISSUE_KEY.BASE_MODAL_SHARE_BELOW_MINIMUM,
                interpolation: { minimum: MIN_BASE_MODAL_SHARE },
            },
        });
    });
});
const MIN_REPLACEMENT_COUNT = 1;
const MAX_REPLACEMENT_COUNT = HEATING_SYSTEM_EXCHANGE_SYSTEM_KEY_LIST.length ** 2;
const NO_OP_CONVERSION_FACTOR = 1;
export const HEATING_SYSTEM_EXCHANGE_ISSUE_KEY = {
    NO_OP_REPLACEMENT: "noOpReplacement",
    DUPLICATE_REPLACEMENT: "duplicateReplacement",
};
const heatingSystemKey = z.custom(isHeatingSystemExchangeSystemKey);
const HeatingSystemReplacementSchema = z.object({
    existingSystem: heatingSystemKey,
    replacedMwh: z.number().positive(),
    newSystem: heatingSystemKey,
});
const isNoOpReplacement = (replacement) => HEATING_SYSTEM_EXCHANGE_SYSTEM_BY_KEY[replacement.existingSystem].energySourceKey ===
    HEATING_SYSTEM_EXCHANGE_SYSTEM_BY_KEY[replacement.newSystem].energySourceKey &&
    computeHeatingSystemExchangeConversionFactor(replacement) === NO_OP_CONVERSION_FACTOR;
const toSystemPairKey = (replacement) => `${replacement.existingSystem}>${replacement.newSystem}`;
const isRepeatedSystemPair = (replacement, index, replacementList) => replacementList.slice(0, index).some((earlier) => toSystemPairKey(earlier) === toSystemPairKey(replacement));
const resolveReplacementIssueKey = (replacement, index, replacementList) => {
    if (isNoOpReplacement(replacement))
        return HEATING_SYSTEM_EXCHANGE_ISSUE_KEY.NO_OP_REPLACEMENT;
    if (isRepeatedSystemPair(replacement, index, replacementList)) {
        return HEATING_SYSTEM_EXCHANGE_ISSUE_KEY.DUPLICATE_REPLACEMENT;
    }
    return null;
};
export const HeatingSystemExchangeParametersSchema = z
    .object({
    replacementList: z.array(HeatingSystemReplacementSchema).min(MIN_REPLACEMENT_COUNT).max(MAX_REPLACEMENT_COUNT),
})
    .superRefine((parameters, ctx) => {
    parameters.replacementList.forEach((replacement, index, replacementList) => {
        const messageKey = resolveReplacementIssueKey(replacement, index, replacementList);
        if (!messageKey)
            return;
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["replacementList", index, "newSystem"],
            params: { messageKey },
        });
    });
});
export const LEVER_PARAMETERS_BY_SLUG = {
    "street-lighting-conversion": StreetLightingConversionParametersSchema,
    "school-daycare-envelope-retrofit": SchoolDaycareEnvelopeRetrofitParametersSchema,
    "administrative-building-envelope-retrofit": AdministrativeBuildingEnvelopeRetrofitParametersSchema,
    "other-municipal-building-envelope-retrofit": OtherMunicipalBuildingEnvelopeRetrofitParametersSchema,
    "heat-behaviour-change-households": HeatBehaviourChangeParametersSchema,
    "heat-behaviour-change-commerce": HeatBehaviourChangeParametersSchema,
    "transport-powertrain-mix": TransportPowertrainMixParametersSchema,
    "transport-modal-split": TransportModalSplitParametersSchema,
    "heat-mix-switch-households": HeatMixSwitchParametersSchema,
    "heat-mix-switch-commerce": HeatMixSwitchParametersSchema,
    "heat-mix-switch-industry": HeatMixSwitchParametersSchema,
    "school-daycare-heating-system-exchange": HeatingSystemExchangeParametersSchema,
    "administrative-building-heating-system-exchange": HeatingSystemExchangeParametersSchema,
    "other-municipal-building-heating-system-exchange": HeatingSystemExchangeParametersSchema,
    "municipal-infrastructure-heating-system-exchange": HeatingSystemExchangeParametersSchema,
};
